<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：会社情報新規登録、編集画面
 *
 * 作成者：2023/06/19 ノア）馬
 * 最終修正者：2023/06/19 ノア）馬
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.companyInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.companyInfo.addEditTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-lr-container">
        <div class="left">
          <div class="content">
            <div>{{ $t('gpsTracker.companyInfo.companyBaseInfoTxt') }}</div>
            <div>{{ $t('gpsTracker.companyInfo.baseBusinessOfficeInfoTxt') }}</div>
          </div>
        </div>
        <div class="right">
          <div>
            <el-form
              label-width="130px"
              label-position="left"
              class="tti-form"
              ref="form"
              :rules="isEdit ? editFormRules : formRules"
              size="mini"
              :model="companyInfoData"
            >
              <div class="content">
                <div class="company-base-info">
                  <div class="tti-form-block">{{ $t('gpsTracker.companyInfo.companyBaseInfoTxt') }}</div>
                  <div class="tti-form-child tti-form-child-level-1">
                    <!-- 会社名 -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.companyName')" prop="companyName">
                      <el-input
                        v-model.trim="companyInfoData.companyName"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbCompanyName')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- 会社コード -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.companyCode')" prop="companyCode">
                      <el-input
                        v-model.trim="companyInfoData.companyCode"
                        class="input"
                        size="small"
                        :disabled="isEdit"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbCompanyCode')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- Prefix -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.companyPrefix')" prop="companyPrefix">
                      <el-input
                        v-model.trim="companyInfoData.companyPrefix"
                        class="input"
                        size="small"
                        :disabled="isEdit"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbPrefix')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- ID上限数 -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.idUpperLimit')" prop="idUpperLimit">
                      <el-input
                        v-model.trim="companyInfoData.idUpperLimit"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbMustNumTip', [
                            $t('gpsTracker.companyInfo.idUpperLimit')
                          ])}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- 端末上限数 -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.terminalUpperLimit')" prop="terminalUpperLimit">
                      <el-input
                        v-model.trim="companyInfoData.terminalUpperLimit"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbMustNumTip', [
                            $t('gpsTracker.companyInfo.terminalUpperLimit')
                          ])}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- 管理対象表示名 -->
                    <el-form-item
                      :label="$t('gpsTracker.companyInfo.managementTargetShowName')"
                      prop="managementTargetShowName"
                    >
                      <el-input
                        v-model.trim="companyInfoData.managementTargetShowName"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbMustTip', [
                            $t('gpsTracker.companyInfo.managementTargetShowName')
                          ])}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                  </div>
                </div>
                <div class="main-eigyousyou-info">
                  <div class="tti-form-block">{{ $t('gpsTracker.companyInfo.baseBusinessOfficeInfoTxt') }}</div>
                  <div class="tti-form-child tti-form-child-level-1">
                    <!-- 営業所名 -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.businessOfficeName')" prop="businessOfficeName">
                      <el-input
                        v-model.trim="companyInfoData.businessOfficeName"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbEigyousyoName')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- 電話番号 -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.tel')" prop="tel">
                      <el-input
                        v-model.trim="companyInfoData.tel"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbEigyousyoTel')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                    <!-- メールアドレス -->
                    <el-form-item :label="$t('gpsTracker.companyInfo.mail')" prop="mail">
                      <el-input
                        v-model.trim="companyInfoData.mail"
                        class="input"
                        size="small"
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.companyInfo.tbEigyousyoEmail')}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      ></el-input>
                    </el-form-item>
                  </div>
                </div>
              </div>
            </el-form>
            <div class="tti-middle-area">
              <div class="tti-bottom-left-area"></div>
              <div class="tti-bottom-right-area">
                <!-- 取消-->
                <el-button
                  class="tti-btn-default tti-btn-001"
                  @click="onCancelButtonClick"
                  v-tooltip="getTooltip(tooltipType.cancel)"
                  ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
                >
                <!-- 保存 -->
                <el-button
                  class="tti-btn-default tti-btn-001"
                  @click="onSaveButtonClick"
                  v-tooltip="getTooltip(tooltipType.save)"
                >
                  <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator'
import { Form } from 'element-ui'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import {
  ToolCompanyMasterData,
  ToolCompanyDetailGetParam,
  ToolCompanyMasterAddParam,
  ToolCompanyDetailUpdateParam
} from '@/models'
import { RULES, validateService, VALID_PATTERN } from '@/service/validateService'
import { addCompanyInfo, getCompanyDetail, updateCompanyInfo } from '@/store/apis'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { cloneDeep } from 'lodash'
import helperService from '@/service/helperService'
import config from '@/config/config'

@Component({
  name: 'TOOL_COMPANY_ADD_EDIT'
})
export default class TOOL_COMPANY_ADD_EDIT extends TOOL_COMPANY_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  /**
   * 画面ID
   */
  pageId = 'TOOL_COMPANY_ADD_EDIT'

  /**
   * 編集
   */
  isEdit = false

  /**
   * 会社情報ID
   */
  companyId = ''

  /**
   * 会社情報
   */
  companyInfoData: ToolCompanyMasterData = {
    id: '',
    companyCode: '',
    companyName: '',
    companyPrefix: '',
    idUpperLimit: '50',
    terminalUpperLimit: '1000',
    managementTargetShowName: '管理対象',
    businessOfficeId: '',
    businessOfficeName: '本社',
    location: '',
    tel: '',
    mail: ''
  }

  /**
   * 古いデータ
   */
  oldData: ToolCompanyMasterData = cloneDeep(this.companyInfoData)

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_COMPANY_ADD_EDIT > mounted')
    this.companyId = this.$route.query.companyId as string
    this.fetchCompanyDetail()
    this.formRef.clearValidate()
  }

  /**
   * 会社情報詳細を取得する
   */
  async fetchCompanyDetail(): Promise<void> {
    if (!this.companyId) {
      return
    }
    const param = {
      companyId: this.companyId
    } as ToolCompanyDetailGetParam

    const res = await getCompanyDetail(param)

    this.companyInfoData = { ...res }

    this.isEdit = true
    this.oldData = cloneDeep(this.companyInfoData)
  }

  /**
   * タイトル表示テキストを取得する
   *
   * @returns {string} タイトル表示テキスト
   */
  get addEditName(): string {
    this.$log.debug('TOOL_COMPANY_ADD_EDIT > addEditName')

    return this.companyId ? i18n.t('buttons.edit').toString() : i18n.t('buttons.newRegist').toString()
  }

  /**
   * 編集時のルール
   */
  editFormRules: Form['rules'] = {
    // 会社名
    companyName: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.companyName')]).toString(),
        trigger: 'blur'
      },
      // 50文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('gpsTracker.companyInfo.companyName'), 50]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 会社コード
    companyCode: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.companyCode')]).toString(),
        trigger: 'blur'
      }
    ],
    // Prefix
    companyPrefix: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.companyPrefix')]).toString(),
        trigger: 'blur'
      },
      // 半角英数字
      {
        pattern: VALID_PATTERN[RULES.ALPHANUMERIC],
        message: i18n
          .t('vaildate.common.alphanumericWithLength', [
            i18n.t('gpsTracker.companyInfo.companyPrefix', [this.companyInfoData.companyPrefix]),
            4
          ])
          .toString(),
        trigger: 'blur'
      },
      // 4文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 4,
            message: i18n
              .t('vaildate.common.alphanumericWithLength', [i18n.t('gpsTracker.companyInfo.companyPrefix'), 4])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // ID上限数
    idUpperLimit: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.idUpperLimit')]).toString(),
        trigger: 'blur'
      },
      // 数字
      {
        pattern: VALID_PATTERN[RULES.NUMERIC],
        message: i18n
          .t('vaildate.common.numberRange', [i18n.t('gpsTracker.companyInfo.idUpperLimit'), 0, 999])
          .toString(),
        trigger: 'blur'
      },
      {
        validator: validateService.getValidator([
          {
            rule: RULES.NUMBERRANGE,
            range: [0, 999],
            message: i18n
              .t('vaildate.common.numberRange', [i18n.t('gpsTracker.companyInfo.idUpperLimit').toString(), 0, 999])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 端末上限数
    terminalUpperLimit: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.terminalUpperLimit')]).toString(),
        trigger: 'blur'
      },
      // 数字
      {
        pattern: VALID_PATTERN[RULES.NUMERIC],
        message: i18n
          .t('vaildate.common.numberRange', [i18n.t('gpsTracker.companyInfo.terminalUpperLimit'), 0, 2147483647])
          .toString(),
        trigger: 'blur'
      },
      {
        validator: validateService.getValidator([
          {
            rule: RULES.NUMBERRANGE,
            range: [0, 2147483647],
            message: i18n
              .t('vaildate.common.numberRange', [
                i18n.t('gpsTracker.companyInfo.terminalUpperLimit').toString(),
                0,
                2147483647
              ])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 管理対象表示名
    managementTargetShowName: [
      // 必須
      {
        required: true,
        message: i18n
          .t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.managementTargetShowName')])
          .toString(),
        trigger: 'blur'
      },
      // 50文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n
              .t('vaildate.common.maxlength', [i18n.t('gpsTracker.companyInfo.managementTargetShowName'), 50])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 営業所名
    businessOfficeName: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.businessOfficeName')]).toString(),
        trigger: 'blur'
      },
      // 50文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n
              .t('vaildate.common.maxlength', [i18n.t('gpsTracker.companyInfo.businessOfficeName'), 50])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 電話番号
    tel: [
      // TEL
      {
        pattern: VALID_PATTERN[RULES.TEL],
        message: i18n.t('vaildate.common.numericAndSymbols', [i18n.t('gpsTracker.companyInfo.tel')]).toString(),
        trigger: 'blur'
      },
      // 50文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('gpsTracker.companyInfo.tel'), 50]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // メールアドレス
    mail: [
      // mail
      {
        pattern: VALID_PATTERN[RULES.EMAIL],
        message: i18n.t('vaildate.common.invalidMail').toString(),
        trigger: 'blur'
      },
      // 50文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('gpsTracker.companyInfo.mail'), 50]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * 新規登録時のルール
   */
  formRules: Form['rules'] = {
    ...this.editFormRules, // 会社コード
    companyCode: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.companyInfo.companyCode')]).toString(),
        trigger: 'blur'
      },
      // 半角数字
      {
        pattern: VALID_PATTERN[RULES.NUMERIC],
        message: i18n
          .t('vaildate.common.maxNumberLength', [i18n.t('gpsTracker.companyInfo.companyCode'), 6])
          .toString(),
        trigger: 'blur'
      },
      // 6文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 6,
            message: i18n
              .t('vaildate.common.maxNumberLength', [i18n.t('gpsTracker.companyInfo.companyCode'), 6])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT >  onCancelButtonClick`)
    if (!helperService.isObjectDataChanged(this.oldData, this.companyInfoData)) {
      this.formRef.clearValidate()
      msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
      return
    }

    const confirmCallback = () => {
      this.formRef.clearValidate()
      this.companyInfoData = {
        ...this.oldData
      }
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {void}
   */
  onSaveButtonClick(): void {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT >  onSaveButtonClick`)

    if (!this.saveDataAfter.name) {
      this.saveDataAfter = async () => {
        if (this.companyId) {
          this.oldData = cloneDeep(this.companyInfoData)
        } else {
          this.companyInfoData = { ...this.oldData }
        }

        msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
      }
    }

    this.formRef.validate(valid => {
      if (!valid) return
      this.saveData()
    })
  }

  /**
   * 遷移前の前処理
   *
   * @returns {Promise<boolean>} 処理結果ステータス
   */
  async saveDataPrepare(): Promise<boolean> {
    try {
      this.formRef.validate(valid => {
        if (!valid) return false
        this.saveData()
      })
      return true
    } catch {
      return false
    }
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveData(): Promise<boolean> {
    try {
      if (this.companyId) {
        if (!helperService.isObjectDataChanged(this.oldData, this.companyInfoData)) {
          msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
          return false
        }
        await this.doUpdateCompanyInfo()
      } else {
        await this.doAddCompanyData()
      }
      await this.saveDataAfter()
      return true
      /* eslint-disable  @typescript-eslint/no-unused-vars */
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      if (e?.data?.common?.code === 'E00101') {
        this.$router.push(config.toolMenu.companyInfo)
        return false
      }
      this.$log.debug(`TOOL_COMPANY_ADD_EDIT > saveTableData: e=`, e)
      throw e
    }
  }

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveDataAfter(): Promise<void> {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT > saveDataAfter`)
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
    if (this.companyId) {
      this.oldData = cloneDeep(this.companyInfoData)
    } else {
      this.companyInfoData = { ...this.oldData }
    }
  }

  /**
   * 会社情報詳細編集。
   *
   *
   */
  async doUpdateCompanyInfo(): Promise<void> {
    this.companyInfoData.idUpperLimit = parseInt(this.companyInfoData.idUpperLimit).toString()
    this.companyInfoData.terminalUpperLimit = parseInt(this.companyInfoData.terminalUpperLimit).toString()
    const companyParam = {
      ...this.companyInfoData
    } as ToolCompanyDetailUpdateParam

    this.$log.debug(`TOOL_COMPANY_ADD_EDIT >  doUpdateCompanyInfo `, companyParam)
    try {
      await updateCompanyInfo(companyParam)
    } catch (e) {
      Vue.$log.debug('TOOL_COMPANY_ADD_EDIT > doUpdateCompanyInfo: e=', e)
      throw e
    }
  }

  /**
   * 会社情報新規登録。
   *
   */
  async doAddCompanyData(): Promise<void> {
    const companyParam = {
      ...this.companyInfoData
    } as ToolCompanyMasterAddParam
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT >  doAddCompanyData `, companyParam)
    try {
      await addCompanyInfo(companyParam)
    } catch (e) {
      Vue.$log.debug('TOOL_COMPANY_ADD_EDIT > doAddCompanyData: e=', e)
      throw e
    }
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT > onBackButtonClick`)

    const doBack = () => {
      this.doBack()
    }

    this.handMove(doBack)
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  doBack(): void {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT > doBack`)
    this.$router.back()
  }

  /**
   * 遷移する時の処理。
   *
   * @param {any} next 遷移処理
   * @param {boolean} needReset 保存失敗またはキャンセル時に、Routeをリセットするかどうか
   * @returns {void}
   */
  // eslint-disable-next-line
  handMove(next: any, needReset = false): void {
    this.$log.debug(`TOOL_COMPANY_ADD_EDIT > handMove`)
    if (!next) {
      return
    }

    if (!helperService.isObjectDataChanged(this.oldData, this.companyInfoData)) {
      next()
      return
    }

    const confirmCallback = async () => {
      this.$log.debug(`TOOL_COMPANY_ADD_EDIT > handMove > confirmCallback`)
      try {
        this.saveDataAfter = async () => {
          msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), next)
        }

        if (!(await this.saveDataPrepare()) && needReset) {
          this.$router.forward()
        }
      } catch (e) {
        this.$log.debug(`TOOL_COMPANY_ADD_EDIT > handMove`, e)
        if (needReset) {
          this.$router.forward()
        }
        throw e
      }
    }

    const rejectCallback = () => {
      this.$log.debug(`TOOL_COMPANY_ADD_EDIT > handMove > rejectCallback`)
      next()
    }

    const cancelCallback = () => {
      this.$log.debug(`TOOL_COMPANY_ADD_EDIT > handMove > cancelCallback`)
      if (!needReset) {
        return
      }

      this.$router.forward()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback,
      cancelCallback
    )
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.tti-form-child {
  margin: 18px 0;
}

.tti-form-child-level-1,
.tti-form-child-level-2 {
  margin-left: 32px;
}

::v-deep .tti-form-child-level-2 label {
  width: 200px;
  text-align: left;
}

.left {
  width: calc(((100% - 24px) * 1) / 5);

  .content {
    padding: 16px 24px;
    background-color: $C04;
  }

  .content div {
    margin: 10px 0;
  }
}

.right {
  width: calc(((100% - 24px) * 4) / 5);

  .content {
    padding: 16px;
  }
}

.main-eigyousyou-info {
  margin-top: 50px;
}

.company-base-info {
  margin-top: 15px;
}

::v-deep .input {
  width: 360px;
}

.tti-form-block {
  border-bottom: $C05 solid 1px;
  margin-bottom: 30px;
  padding-bottom: 5px;
}

.tti-middle-area {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  margin-right: 17px;

  .tti-bottom-left-area,
  .tti-bottom-right-area {
    display: flex;
    align-items: center;
  }
}
</style>
